import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  QUIT_CURRENCIES,
  type QuitStats,
  cleanDailyCost,
  defaultCurrency,
  formatMoney,
  moneySaved,
} from '@goaltree/backend/convex/lib/quitHabit'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { ChevronDown, CigaretteOff, WineOff } from 'lucide-react'
import { type ComponentProps, useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { patchHabit } from '@/lib/goals-optimistic'

export function useQuitHabit(habitId: string | undefined) {
  const habits = useQuery(api.nodes.getHabits, habitId ? {} : 'skip')
  return habitId ? (habits?.find((h) => h._id === habitId) ?? null) : null
}

const deviceCurrency = () =>
  defaultCurrency(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    typeof navigator === 'undefined' ? [] : navigator.languages,
  )

function useSaved(quit: QuitStats) {
  const { i18n } = useTranslation()
  const amount = moneySaved(quit.freeDays, quit.dailyCost)
  return amount !== null && quit.currency ? formatMoney(amount, quit.currency, i18n.language) : null
}

function QuitIcon({
  kind,
  ...props
}: { kind: QuitStats['kind'] } & ComponentProps<typeof CigaretteOff>) {
  return kind === 'alcohol' ? <WineOff {...props} /> : <CigaretteOff {...props} />
}

// Row meta: "23 days free · ¥13,800 saved".
export function QuitChip({ habitId }: { habitId: string }) {
  const quit = useQuitHabit(habitId)?.quit
  return quit ? <QuitChipText quit={quit} /> : null
}

function QuitChipText({ quit }: { quit: QuitStats }) {
  const { t } = useTranslation()
  const saved = useSaved(quit)
  return (
    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap leading-none font-semibold text-emerald-700 dark:text-emerald-400">
      <QuitIcon kind={quit.kind} className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} />
      {t('quit.row', { count: quit.days })}
      {saved && ` · ${t('quit.saved', { amount: saved })}`}
    </span>
  )
}

export function QuitHero({
  habitId,
  quit,
  className,
}: {
  habitId: Id<'nodes'>
  quit: QuitStats
  className?: string
}) {
  const { t, i18n } = useTranslation()
  const saved = useSaved(quit)
  const slipped = quit.days === 0
  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.07] px-4 py-3',
        className,
      )}
    >
      {slipped ? (
        <p className="text-sm font-semibold leading-snug text-foreground">
          {quit.freeDays > 0 ? t('quit.slip', { count: quit.best }) : t('quit.dayOne')}
        </p>
      ) : (
        <div className="flex items-baseline gap-2">
          <span className="text-5xl font-black leading-none tabular-nums text-foreground">
            {new Intl.NumberFormat(i18n.language).format(quit.days)}
          </span>
          <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">
            {t(`quit.days.${quit.kind}`, { count: quit.days })}
          </span>
        </div>
      )}
      {((!slipped && quit.best > quit.days) || saved) && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11px] text-muted-foreground">
          {!slipped && quit.best > quit.days && <span>{t('quit.best', { count: quit.best })}</span>}
          {saved && (
            <span className="font-bold text-foreground">
              {t('quit.savedOver', { amount: saved, count: quit.freeDays })}
            </span>
          )}
        </div>
      )}
      <QuitCostInput habitId={habitId} quit={quit} />
    </div>
  )
}

function QuitCostInput({ habitId, quit }: { habitId: Id<'nodes'>; quit: QuitStats }) {
  const { t, i18n } = useTranslation()
  const setQuit = useMutation(api.nodes.setQuitHabit).withOptimisticUpdate(
    (store, { dailyCost, currency }) =>
      patchHabit(store, habitId, (h) => {
        if (!h.quit) return {}
        const cost =
          dailyCost === undefined ? h.quit.dailyCost : (cleanDailyCost(dailyCost) ?? null)
        return {
          quit: {
            ...h.quit,
            dailyCost: cost,
            currency: cost ? (currency ?? h.quit.currency) : null,
          },
        }
      }),
  )
  const inputId = useId()
  const [picked, setPicked] = useState<string | null>(null)
  const currency = picked ?? quit.currency ?? deviceCurrency()
  const [value, setValue] = useState(quit.dailyCost?.toString() ?? '')
  useEffect(() => setValue(quit.dailyCost?.toString() ?? ''), [quit.dailyCost])
  const symbol =
    (() => {
      try {
        return new Intl.NumberFormat(i18n.language, { style: 'currency', currency })
          .formatToParts(0)
          .find((p) => p.type === 'currency')?.value
      } catch {
        return null
      }
    })() ?? currency

  const save = () => {
    const n = value.trim() === '' ? null : Number(value.replace(/[^\d.]/g, ''))
    if ((cleanDailyCost(n) ?? null) === quit.dailyCost) return
    void setQuit({ taskId: habitId, dailyCost: n, currency })
  }

  return (
    <label
      htmlFor={inputId}
      className="flex items-center gap-2 border-t border-emerald-500/15 pt-2 text-xs text-muted-foreground"
    >
      <span className="min-w-0 flex-1 truncate">
        {quit.dailyCost ? t('quit.cost') : t('quit.addCost')}
      </span>
      <span className="flex shrink-0 items-center gap-1 rounded-md border border-border bg-card px-2 py-1 focus-within:ring-2 focus-within:ring-ring/50">
        <span className="relative flex items-center gap-0.5 font-mono text-foreground/70 hover:text-foreground">
          {symbol}
          <ChevronDown className="h-2.5 w-2.5 opacity-60" />
          <select
            aria-label={t('quit.currency')}
            title={t('quit.currency')}
            value={currency}
            onChange={(e) => {
              setPicked(e.target.value)
              if (quit.dailyCost) void setQuit({ taskId: habitId, currency: e.target.value })
            }}
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-0 cursor-pointer opacity-0"
          >
            {(QUIT_CURRENCIES.includes(currency)
              ? QUIT_CURRENCIES
              : [currency, ...QUIT_CURRENCIES]
            ).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </span>
        <input
          id={inputId}
          type="text"
          inputMode="decimal"
          aria-label={t('quit.cost')}
          title={t('quit.costHint')}
          placeholder="0"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
          }}
          onClick={(e) => e.stopPropagation()}
          className="w-16 bg-transparent text-right font-mono text-foreground tabular-nums outline-none placeholder:text-muted-foreground/50"
        />
      </span>
    </label>
  )
}

export function QuitToggleRow({ habitId }: { habitId: Id<'nodes'> }) {
  const { t } = useTranslation()
  const habit = useQuitHabit(habitId)
  const setQuit = useMutation(api.nodes.setQuitHabit)
  if (!habit) return null
  const on = !!habit.quit
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-border/60 px-3 py-2">
      <QuitIcon
        kind={habit.quit?.kind ?? 'free'}
        className="h-4 w-4 shrink-0 text-muted-foreground"
      />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-foreground">{t('quit.track')}</p>
        <p className="truncate text-[11px] text-muted-foreground">{t('quit.trackDesc')}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={t('quit.track')}
        onClick={() => void setQuit({ taskId: habitId, quit: !on })}
        className={cn(
          'relative h-6 w-10 shrink-0 cursor-pointer rounded-full transition-colors',
          on ? 'bg-emerald-500' : 'bg-foreground/15',
        )}
      >
        <span
          className="absolute top-[3px] size-[18px] rounded-full bg-background shadow-sm transition-[left]"
          style={{ left: on ? 19 : 3 }}
        />
      </button>
    </div>
  )
}
